import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Monitor, Search, ExternalLink, X, Zap,
  Circle, Loader2, Sparkles, GitCompare,
  FileText, BookOpen, Bookmark, MoreVertical, RefreshCw, Trash2, Eye,
  CheckCircle2, AlertCircle, Folder
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmbeddedSkillRail, WorkspacePatternsPanel, parseWorkspacePatternsFromSkillData } from '@/components/skill';
import type { WorkspacePattern } from '@/components/skill';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { api } from '@/services/client';
import { tabService, type AISuggestedGroup } from '@/services/tabs';
import { appService } from '@/services/apps';
import { apiClient } from '@/services/api-client';
import { wsService } from '@/services/websocket.service';
import type { ApiBrowserTab } from '@/types';
import { toast } from 'sonner';
import { useTabMonitorSession, WS_SESSION_STORAGE_KEY } from '@/context/TabMonitorSessionContext';
import { closeBrowserTabs } from '@/services/extensionBridge';
import {
  countFailedScans,
  getCachedTabSecurity,
  scanTabsSecurity,
  unscannedTabSecurity,
  type TabSecurityInfo,
  type TabSecurityStatus,
} from '@/components/tabs/tabSecurity';
import { EncryptionBadge, SecurityScoreBar } from '@/components/tabs/TabSecurityCells';
import { TabSecurityTable, toSecurityRow } from '@/components/tabs/TabSecurityTable';
import { TabAlertsPanel } from '@/components/tabs/TabAlertsPanel';
import { isPersonalOrSensitiveUrl } from '@/utils/personalTabUrls';

interface Tab {
  id: string;
  /** Chrome `tabs.id` when synced from the extension */
  browserTabId?: string;
  url: string;
  title: string;
  favicon?: string;
  isActive: boolean;
  lastAccessed: Date;
  clusterId?: string;
  summary?: string;
  entities?: string[];
  sentiment?: 'positive' | 'neutral' | 'negative';
  security?: TabSecurityInfo;
}

interface TabCluster {
  id: string;
  name: string;
  pattern: string;
  tabs: Tab[];
  confidence: number;
  suggestedAction?: string;
}


/** Raw tab-intelligence group (legacy + Go models.TabGroup JSON). */
type RawTabGroup = {
  id: string;
  name?: string;
  group_name?: string;
  pattern?: string;
  group_type?: string;
  tabs?: ApiBrowserTab[];
  tab_ids?: Array<string | number>;
  confidence?: number;
  confidence_score?: number;
};

interface GenerationProgress {
  clusterId: string;
  stage: string;
  percent: number;
  message: string;
  appId?: string;
  error?: string;
}

type TabGroupFilter = 'all' | 'active' | 'clustered' | 'ungrouped';
type SecurityFilter = 'all' | TabSecurityStatus;

function tabHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
}

const filterSelectClass =
  'px-3 py-3 min-w-[7.5rem] bg-khiimori-bg-elevated/50 border border-khiimori-border/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 text-sm text-khiimori-text-primary backdrop-blur-xl [&>option]:bg-khiimori-bg-elevated';

/** API may return embedded `tabs` (legacy) or `tab_ids` (Go TabGroup); names from `name` or `group_name`. */
function tabClustersFromApiGroups(groups: RawTabGroup[] | undefined, sessionTabs: Tab[]): TabCluster[] {
  const tabById = new Map(sessionTabs.map((t) => [t.id, t]));
  return (groups ?? []).map((g) => {
    const fromApiTabs = Array.isArray(g.tabs) ? g.tabs : [];
    const tabIdsRaw = Array.isArray(g.tab_ids) ? g.tab_ids : [];
    const tabIds = tabIdsRaw.map((id) => String(id));
    let tabsForCluster: Tab[];
    if (fromApiTabs.length > 0) {
      tabsForCluster = fromApiTabs.map((t) => ({
        id: t.id,
        browserTabId: t.tab_id ? String(t.tab_id) : undefined,
        url: t.url,
        title: t.title || '(untitled)',
        favicon: t.favicon,
        isActive: !t.status || t.status === 'active',
        lastAccessed: t.created_at ? new Date(t.created_at) : new Date(),
      }));
    } else {
      tabsForCluster = tabIds
        .map((id) => tabById.get(id))
        .filter((t): t is Tab => t !== undefined);
    }
    const pattern = g.pattern ?? g.group_type ?? 'custom';
    const name = g.name ?? g.group_name ?? 'Untitled group';
    const confidence =
      typeof g.confidence === 'number'
        ? g.confidence
        : typeof g.confidence_score === 'number'
          ? g.confidence_score
          : 0.85;
    return {
      id: g.id,
      name,
      pattern,
      tabs: tabsForCluster,
      confidence,
      suggestedAction:
        pattern === 'product_comparison'
          ? 'Generate Comparison Table'
          : pattern === 'research'
            ? 'Create Research Summary'
            : pattern === 'shopping'
              ? 'Create Price Comparison'
              : 'Generate App',
    };
  });
}

function assignTabClusterIds(tabs: Tab[], clusters: TabCluster[]): Tab[] {
  const clusterIdByTabId = new Map<string, string>();
  for (const c of clusters) {
    for (const t of c.tabs) {
      clusterIdByTabId.set(t.id, c.id);
    }
  }
  return tabs.map((t) => ({
    ...t,
    clusterId: clusterIdByTabId.get(t.id),
  }));
}

function apiErrorMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const data = (e as { response?: { data?: { error?: string } } }).response?.data;
    if (data?.error) return data.error;
  }
  return e instanceof Error ? e.message : fallback;
}

function appsSectionPath(_appType: string): string {
  return '/apps?section=apps';
}

const APP_GENERATION_OPTIONS = [
  { value: 'comparison', label: 'Comparison Table' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'research_summary', label: 'Research Synthesis' },
  { value: 'research_paper', label: 'Research Paper' },
  { value: 'data_aggregation', label: 'Data Aggregation' },
  { value: 'shopping', label: 'Price Comparison' },
  { value: 'custom', label: 'Custom App' },
  { value: 'dashboard', label: 'Dashboard' },
] as const;

type AppGenerationType = (typeof APP_GENERATION_OPTIONS)[number]['value'];

function resolveAppGeneration(
  appType: AppGenerationType,
  fallbackName?: string,
): { backendType: string; appName: string; minTabs: number } {
  let appName = fallbackName || 'Generated App';
  switch (appType) {
    case 'comparison':
      appName = fallbackName || 'Comparison Table';
      break;
    case 'timeline':
      appName = fallbackName || 'Timeline';
      break;
    case 'research_summary':
      appName = fallbackName || 'Research Synthesis';
      break;
    case 'research_paper':
      appName = fallbackName || 'Research Paper';
      break;
    case 'data_aggregation':
      appName = fallbackName || 'Data Aggregation';
      break;
    case 'shopping':
      appName = fallbackName || 'Price Comparison';
      break;
    case 'dashboard':
      appName = fallbackName || 'Dashboard App';
      break;
    case 'custom':
      appName = fallbackName || 'Custom Generated App';
      break;
  }
  const backendType = appType === 'shopping' ? 'comparison' : appType;
  const minTabs =
    appType === 'comparison' ||
    appType === 'research_summary' ||
    appType === 'research_paper' ||
    appType === 'shopping' ||
    appType === 'data_aggregation'
      ? 2
      : 1;
  return { backendType, appName, minTabs };
}

function defaultAppTypeForCluster(pattern: string): AppGenerationType {
  return 'custom';
}

export function TabMonitor() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { workspaceSessionId, refreshSessionById } = useTabMonitorSession();
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [clusters, setClusters] = useState<TabCluster[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBy, setFilterBy] = useState<TabGroupFilter>('all');
  const [securityFilter, setSecurityFilter] = useState<SecurityFilter>('all');
  const [siteFilter, setSiteFilter] = useState<string>('all');
  const [selectedTabs, setSelectedTabs] = useState<Set<string>>(new Set());
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'clusters'>('list');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingApp, setIsGeneratingApp] = useState(false);
  const [generationMessage, setGenerationMessage] = useState<string | null>(null);
  const [activeGenerationType, setActiveGenerationType] = useState<string | null>(null);
  const [generatingClusterId, setGeneratingClusterId] = useState<string | null>(null);
  const [generationProgress, setGenerationProgress] = useState<Record<string, GenerationProgress>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isScanningSecurity, setIsScanningSecurity] = useState(false);
  const [workspacePatterns, setWorkspacePatterns] = useState<WorkspacePattern[]>([]);
  const [clusterAppTypes, setClusterAppTypes] = useState<Record<string, AppGenerationType>>({});
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [showAISuggestModal, setShowAISuggestModal] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestedGroup[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCreatingKey, setAiCreatingKey] = useState<string | null>(null);

  const handleSkillExecuted = useCallback((skill: { id: string }, result: { status: string; data?: Record<string, unknown> }) => {
    if (skill.id !== 'analyze_workspace' || result.status !== 'success') return;
    const parsed = parseWorkspacePatternsFromSkillData(result.data);
    setWorkspacePatterns(parsed);
  }, []);

  const fetchTabClusters = useCallback(async (sessionId: string, sessionTabs: Tab[]): Promise<TabCluster[]> => {
    try {
      const res = await api.get<{ groups: RawTabGroup[] }>(`/tabs-intelligence/groups/${sessionId}`);
      const mappedClusters = tabClustersFromApiGroups(res.data?.groups ?? [], sessionTabs);
      setClusters(mappedClusters);
      return mappedClusters;
    } catch (e) {
      console.error('Failed to fetch tab clusters:', e);
      toast.error(e instanceof Error ? e.message : 'Failed to load tab groups');
      return [];
    }
  }, []);

  // Scans tabs via the backend and merges results into state. Results are
  // only applied while the tab still has the URL that was scanned.
  const applySecurityScans = useCallback(
    async (list: { id: string; url: string }[], force = false) => {
      const results = await scanTabsSecurity(list, { force });
      const scannedUrl = new Map(list.map((t) => [t.id, t.url]));
      setTabs((prev) =>
        prev.map((t) => {
          const info = results.get(t.id);
          return info && scannedUrl.get(t.id) === t.url ? { ...t, security: info } : t;
        }),
      );
      return results;
    },
    [],
  );

  const fetchTabs = useCallback(async (sessionId: string, opts?: { isRetry?: boolean; silent?: boolean }) => {
    if (!opts?.silent) {
      setIsLoading(true);
      // Reset generation state when switching sessions to avoid stale progress from previous session
      setGeneratingClusterId(null);
      setGenerationProgress({});
    }
    try {
      const { tabs: apiTabs } = await tabService.getTabsBySession(sessionId, 1, 100);
      const mappedTabs: Tab[] = apiTabs
        .filter((t: ApiBrowserTab) => !isPersonalOrSensitiveUrl(t.url))
        .map((t: ApiBrowserTab) => ({
        id: t.id,
        browserTabId: t.tab_id ? String(t.tab_id) : undefined,
        url: t.url,
        title: t.title || '(untitled)',
        favicon: t.favicon,
        isActive: !t.status || t.status === 'active',
        lastAccessed: t.created_at ? new Date(t.created_at) : new Date(),
        security: getCachedTabSecurity(t.id, t.url) ?? unscannedTabSecurity(t.url),
      }));
      const clusterList = await fetchTabClusters(sessionId, mappedTabs);
      const nextTabs = assignTabClusterIds(mappedTabs, clusterList);
      setTabs((prev) => {
        if (opts?.silent && prev.length > 0) {
          const prevKey = prev.map((t) => `${t.id}:${t.url}:${t.title}`).join('|');
          const nextKey = nextTabs.map((t) => `${t.id}:${t.url}:${t.title}`).join('|');
          if (prevKey === nextKey) return prev;
        }
        return nextTabs;
      });
      setFetchError(null);
      void applySecurityScans(nextTabs);
    } catch (e) {
      console.error('Failed to fetch tabs:', e);
      const msg = e instanceof Error ? e.message : String(e);
      const forbidden = /forbidden|403/i.test(msg);
      if (forbidden && !opts?.isRetry) {
        setFetchError(null);
        toast.error('Workspace session was out of date; refreshing…');
      } else if (!opts?.isRetry) {
        setFetchError(msg);
        toast.error(`Failed to load tabs: ${msg}`);
      }
      if (forbidden && !opts?.isRetry) {
        sessionStorage.removeItem(WS_SESSION_STORAGE_KEY);
        try {
          const cur = await api.get<{ session: { id: string } }>('/sessions/current');
          const id = cur.data.session.id;
          await refreshSessionById(id);
          await fetchTabs(id, { isRetry: true });
        } catch (e2) {
          console.error('Failed to refresh workspace session after forbidden:', e2);
          toast.error(e2 instanceof Error ? e2.message : 'Failed to refresh workspace session');
        }
      }
    } finally {
      if (!opts?.silent) {
        setIsLoading(false);
      }
    }
  }, [fetchTabClusters, refreshSessionById, applySecurityScans]);

  useEffect(() => {
    if (!workspaceSessionId) return;
    void fetchTabs(workspaceSessionId);
    // Reset user-controlled state on session switch
    setClusters([]);
    setSelectedTabs(new Set());
    setFilterBy('all');
    setSecurityFilter('all');
    setSiteFilter('all');
    setSearchQuery(searchParams.get('q') ?? '');
  }, [workspaceSessionId, fetchTabs, searchParams]);

  useEffect(() => {
    const view = searchParams.get('view');
    if (view === 'list' || view === 'clusters' || view === 'grid') {
      setViewMode(view);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!workspaceSessionId) return;
    const id = workspaceSessionId;

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        void fetchTabs(id, { silent: true });
      }
    };

    const onExtensionSync = (event: MessageEvent) => {
      if (event.source !== window) return;
      if (event.data?.type === 'KHIIMORI_TAB_PREVIEW_UPDATED') {
        refreshIfVisible();
      }
    };

    const interval = setInterval(refreshIfVisible, 10000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void fetchTabs(id, { silent: true });
      }
    };
    window.addEventListener('message', onExtensionSync);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener('message', onExtensionSync);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [workspaceSessionId, fetchTabs]);

  // Generation events are subscribed per-cluster in handleClusterSuggestedAction.
  // No global subscription to avoid double-firing with SSE.

  const analyzeTab = async (tabId: string) => {
    setIsAnalyzing(true);
    try {
      const analysis = await tabService.analyzeTab(tabId);
      setTabs(prev => prev.map(t => t.id === tabId ? {
        ...t,
        summary: analysis.summary,
        entities: analysis.entities,
        sentiment: analysis.sentiment as 'positive' | 'neutral' | 'negative',
      } : t));
      toast.success('Tab analyzed successfully');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to analyze tab');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateResearchPaperFromTabs = async (
    tabIds: string[],
    appName: string,
    opts?: { minTabs?: number; clearSelection?: boolean; progressKey?: string; clusterId?: string },
  ) => {
    const minTabs = opts?.minTabs ?? 2;
    if (!workspaceSessionId) {
      toast.error('No workspace session');
      return;
    }
    if (tabIds.length < minTabs) {
      toast.error(minTabs === 2 ? 'Select at least 2 tabs' : 'Select at least 1 tab');
      return;
    }

    setIsGeneratingApp(true);
    setActiveGenerationType('research_paper');
    setGenerationMessage('Writing research paper…');
    if (opts?.clusterId) {
      setGeneratingClusterId(opts.clusterId);
    }
    if (opts?.progressKey && opts.clusterId) {
      setGenerationProgress((prev) => ({
        ...prev,
        [opts.progressKey!]: {
          clusterId: opts.clusterId!,
          stage: 'generating',
          percent: 40,
          message: 'Generating research paper…',
        },
      }));
    }

    try {
      const paper = await apiClient.createResearchPaper({
        session_id: workspaceSessionId,
        tab_ids: tabIds,
        title: appName,
        journal_format: 'ieee',
      });
      const label = paper.title || appName;
      if (opts?.progressKey && opts.clusterId) {
        setGenerationProgress((prev) => ({
          ...prev,
          [opts.progressKey!]: {
            clusterId: opts.clusterId!,
            stage: 'complete',
            percent: 100,
            message: 'Research paper ready',
          },
        }));
      }
      toast.success(`${label} ready`, {
        description: 'Open it in Apps to view the manuscript.',
        action: {
          label: paper.app_id ? 'Open paper' : 'View apps',
          onClick: () =>
            navigate(paper.app_id ? `/apps/${paper.app_id}` : appsSectionPath('research_paper')),
        },
      });
      if (opts?.clearSelection !== false) {
        setSelectedTabs(new Set());
      }
      if (paper.app_id) {
        navigate(`/apps/${paper.app_id}`);
      } else {
        navigate('/apps/synthesize');
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to generate research paper'));
      if (opts?.progressKey && opts.clusterId) {
        setGenerationProgress((prev) => ({
          ...prev,
          [opts.progressKey!]: {
            clusterId: opts.clusterId!,
            stage: 'error',
            percent: 0,
            message: 'Failed to generate research paper',
          },
        }));
      }
    } finally {
      setIsGeneratingApp(false);
      setGenerationMessage(null);
      setActiveGenerationType(null);
      setGeneratingClusterId(null);
    }
  };

  const generateFromTabs = async (
    tabIds: string[],
    appType: string,
    appName: string,
    opts?: { minTabs?: number; clearSelection?: boolean },
  ) => {
    if (appType === 'research_paper') {
      await generateResearchPaperFromTabs(tabIds, appName, opts);
      return;
    }
    const minTabs = opts?.minTabs ?? 1;
    if (!workspaceSessionId) {
      toast.error('No workspace session');
      return;
    }
    if (tabIds.length < minTabs) {
      toast.error(minTabs === 2 ? 'Select at least 2 tabs' : 'Select at least 1 tab');
      return;
    }

    setIsGeneratingApp(true);
    setActiveGenerationType(appType);
    setGenerationMessage('Starting…');
    try {
      const app = await appService.createSessionApp(
        workspaceSessionId,
        {
          tab_ids: tabIds,
          app_type: appType,
          app_name: appName,
        },
        {
          onProgress: (_percent, message) => setGenerationMessage(message || 'Generating…'),
        },
      );
      const label = app.name || appName;
      toast.success(`${label} ready`, {
        description: 'Open it in Apps to view the result.',
        action: {
          label: 'View apps',
          onClick: () => navigate(appsSectionPath(appType)),
        },
      });
      if (opts?.clearSelection !== false) {
        setSelectedTabs(new Set());
      }
      navigate(appsSectionPath(appType));
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to generate app'));
    } finally {
      setIsGeneratingApp(false);
      setGenerationMessage(null);
      setActiveGenerationType(null);
    }
  };

  const handleBulkCompare = async () => {
    await generateFromTabs(Array.from(selectedTabs), 'comparison', 'Tab Comparison', { minTabs: 2 });
  };

  const handleBulkSynthesize = async () => {
    await generateFromTabs(Array.from(selectedTabs), 'research_summary', 'Research Synthesis', { minTabs: 2 });
  };

  const handleBulkResearchPaper = async () => {
    await generateFromTabs(Array.from(selectedTabs), 'research_paper', 'Research Paper', { minTabs: 2 });
  };

  const handleBulkGenerateApp = async (appType: AppGenerationType) => {
    // Data Aggregation is a dedicated interactive tool at /insights?section=data,
    // not an "app" you can materialize from a single API call. Hand off to it
    // so the user gets the same workflow as from the Insights hub.
    if (appType === 'data_aggregation') {
      const ids = Array.from(selectedTabs);
      if (ids.length < 2) {
        toast.error('Select at least 2 tabs to aggregate');
        return;
      }
      try {
        sessionStorage.setItem('khiimori.dataAggregation.tabIds', JSON.stringify(ids));
      } catch {
        // ignore — the tool will still load its own tabs
      }
      navigate('/insights?section=data');
      return;
    }
    const { backendType, appName, minTabs } = resolveAppGeneration(appType);
    await generateFromTabs(Array.from(selectedTabs), backendType, appName, { minTabs });
  };

  const handleClusterGenerateApp = async (cluster: TabCluster, appType: AppGenerationType) => {
    if (appType === 'data_aggregation') {
      const ids = cluster.tabs.map((t) => t.id).filter(Boolean);
      if (ids.length < 2) {
        toast.error('This group needs at least 2 tabs to aggregate');
        return;
      }
      try {
        sessionStorage.setItem('khiimori.dataAggregation.tabIds', JSON.stringify(ids));
      } catch {
        // ignore
      }
      navigate('/insights?section=data');
      return;
    }
    if (!workspaceSessionId) {
      toast.error('No workspace session');
      return;
    }
    const tab_ids = cluster.tabs.map((t) => t.id);
    if (tab_ids.length === 0) {
      toast.error('This group has no tabs to generate from');
      return;
    }

    const { backendType, appName, minTabs } = resolveAppGeneration(appType, cluster.name);

    if (tab_ids.length < minTabs) {
      toast.error(
        minTabs === 2
          ? 'This group needs at least 2 tabs for this app type'
          : 'This group has no tabs to generate from',
      );
      return;
    }

    if (backendType === 'research_paper') {
      const progressKey = `${workspaceSessionId}-${cluster.id}`;
      await generateResearchPaperFromTabs(tab_ids, appName, {
        minTabs,
        clearSelection: false,
        progressKey,
        clusterId: cluster.id,
      });
      return;
    }

    const progressKey = `${workspaceSessionId}-${cluster.id}`;
    setGeneratingClusterId(cluster.id);
    setGenerationProgress(prev => ({
      ...prev,
      [progressKey]: { clusterId: cluster.id, stage: 'generating', percent: 50, message: 'Generating app...' },
    }));

    try {
      const app = await appService.createSessionApp(workspaceSessionId, {
        tab_ids,
        app_type: backendType,
        app_name: appName,
      });
      setGenerationProgress(prev => ({
        ...prev,
        [progressKey]: {
          clusterId: cluster.id,
          stage: 'complete',
          percent: 100,
          message: 'App generated!',
        },
      }));
      toast.success(`${app.name || appName} ready`, {
        description: 'Open it in Apps to view the result.',
        action: {
          label: 'View apps',
          onClick: () => navigate(appsSectionPath(backendType)),
        },
      });
      navigate(appsSectionPath(backendType));
    } catch (e) {
      setGenerationProgress(prev => ({
        ...prev,
        [progressKey]: {
          clusterId: cluster.id,
          stage: 'error',
          percent: 0,
          message: 'Generation failed',
        },
      }));
      toast.error(apiErrorMessage(e, 'Failed to generate app'));
    } finally {
      setGeneratingClusterId(null);
    }
  };

  const handleBulkSave = async () => {
    if (selectedTabs.size === 0) {
      toast.error('Select at least 1 tab to save');
      return;
    }
    try {
      const promises = Array.from(selectedTabs).map(tabId =>
        api.post('/bookmarks', { tab_id: tabId })
      );
      await Promise.all(promises);
      toast.success(`${selectedTabs.size} tab(s) saved to bookmarks`);
      setSelectedTabs(new Set());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save bookmarks');
    }
  };

  const handleDeleteTab = (tabId: string) => {
    setSelectedTabId(tabId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTab = async () => {
    if (!selectedTabId) return;
    const tab = tabs.find((t) => t.id === selectedTabId);
    try {
      // Close the live browser tab first so extension sync does not resurrect this row.
      if (tab) {
        closeBrowserTabs({
          chromeTabIds: tab.browserTabId ? [tab.browserTabId] : [],
          urls: [tab.url],
          forceCloseUrls: true,
          singleUrlMatch: true,
        });
      }
      await tabService.deleteTab(selectedTabId);
      setTabs((prev) => prev.filter((t) => t.id !== selectedTabId));
      toast.success('Tab deleted');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete tab');
    }
    setShowDeleteConfirm(false);
    setSelectedTabId(null);
  };

  const toggleTabSelection = (tabId: string) => {
    const newSelection = new Set(selectedTabs);
    if (newSelection.has(tabId)) {
      newSelection.delete(tabId);
    } else {
      newSelection.add(tabId);
    }
    setSelectedTabs(newSelection);
  };

  const handleScanAllSecurity = async () => {
    setIsScanningSecurity(true);
    try {
      const results = await applySecurityScans(tabs, true);
      const failed = countFailedScans(results);
      if (failed > 0) {
        toast.error(`Scan finished; ${failed} tab(s) could not be scanned`);
      } else {
        toast.success('Security scan complete');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Security scan failed');
    } finally {
      setIsScanningSecurity(false);
    }
  };

  const siteOptions = useMemo(() => {
    const hosts = new Set<string>();
    for (const tab of tabs) {
      const host = tabHost(tab.url);
      if (host) hosts.add(host);
    }
    return [...hosts].sort((a, b) => a.localeCompare(b));
  }, [tabs]);

  const hasActiveFilters =
    filterBy !== 'all' || securityFilter !== 'all' || siteFilter !== 'all' || searchQuery.trim() !== '';

  const clearFilters = () => {
    setSearchQuery('');
    setFilterBy('all');
    setSecurityFilter('all');
    setSiteFilter('all');
  };

  const filteredTabs = tabs.filter((tab) => {
    const query = searchQuery.trim().toLowerCase();
    if (query && !tab.title.toLowerCase().includes(query) && !tab.url.toLowerCase().includes(query)) {
      return false;
    }
    if (filterBy === 'active' && !tab.isActive) return false;
    if (filterBy === 'clustered' && !tab.clusterId) return false;
    if (filterBy === 'ungrouped' && tab.clusterId) return false;
    if (securityFilter !== 'all' && tab.security?.status !== securityFilter) return false;
    if (siteFilter !== 'all' && tabHost(tab.url) !== siteFilter) return false;
    return true;
  });

  const handleSelectAll = (select: boolean) => {
    if (select) {
      setSelectedTabs(new Set(filteredTabs.map((t) => t.id)));
    } else {
      setSelectedTabs(new Set());
    }
  };

  const securityTableRows = useMemo(
    () => filteredTabs.map((tab) => toSecurityRow(tab, tab.security)),
    [filteredTabs],
  );

  const alertSessionTabs = useMemo(
    () =>
      tabs.map((tab) => ({
        id: tab.id,
        title: tab.title,
        url: tab.url,
        created_at: tab.lastAccessed.toISOString(),
      })),
    [tabs],
  );

  const unclusteredTabs = useMemo(() => tabs.filter((t) => !t.clusterId), [tabs]);
  const selectedUnclusteredTabIds = useMemo(
    () => Array.from(selectedTabs).filter((id) => unclusteredTabs.some((t) => t.id === id)),
    [selectedTabs, unclusteredTabs],
  );

  const suggestionKey = (s: AISuggestedGroup) => `${s.groupName}|${s.tabIds.join(',')}`;

  const handleOpenCreateGroupModal = () => {
    if (selectedUnclusteredTabIds.length === 0) {
      toast.error('Select at least one unclustered tab');
      return;
    }
    const firstTitle = unclusteredTabs.find((t) => selectedUnclusteredTabIds.includes(t.id))?.title;
    setNewGroupName(firstTitle ? `${firstTitle.slice(0, 40)} group` : 'New Group');
    setShowCreateGroupModal(true);
  };

  const handleCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name) {
      toast.error('Enter a group name');
      return;
    }
    if (!workspaceSessionId) {
      toast.error('No workspace session');
      return;
    }
    if (selectedUnclusteredTabIds.length === 0) {
      toast.error('Select at least one unclustered tab');
      return;
    }
    setIsCreatingGroup(true);
    try {
      await tabService.createTabGroup(name, selectedUnclusteredTabIds, workspaceSessionId);
      toast.success(`Created group "${name}"`);
      setShowCreateGroupModal(false);
      setNewGroupName('');
      setSelectedTabs(new Set());
      await fetchTabs(workspaceSessionId, { silent: true });
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to create group'));
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const handleAISuggestGroups = async () => {
    if (!workspaceSessionId) {
      toast.error('No workspace session');
      return;
    }
    setShowAISuggestModal(true);
    setAiLoading(true);
    try {
      const suggestions = await tabService.getGroupSuggestions(workspaceSessionId);
      setAiSuggestions(suggestions);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to get AI suggestions'));
      setAiSuggestions([]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAcceptSuggestion = async (suggestion: AISuggestedGroup) => {
    if (!workspaceSessionId) return;
    const key = suggestionKey(suggestion);
    setAiCreatingKey(key);
    try {
      await tabService.createTabGroup(suggestion.groupName, suggestion.tabIds, workspaceSessionId);
      toast.success(`Created group "${suggestion.groupName}"`);
      setAiSuggestions((prev) => prev.filter((s) => suggestionKey(s) !== key));
      await fetchTabs(workspaceSessionId, { silent: true });
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to create group'));
    } finally {
      setAiCreatingKey(null);
    }
  };

  return (
    <div className="text-khiimori-text-primary">
      <div className="mb-6">
        {/* Controls */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-khiimori-text-muted z-10" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tabs by title or URL..."
                className="w-full pl-10 pr-4 py-3 bg-khiimori-bg-elevated/50 border border-khiimori-border/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 text-khiimori-text-primary placeholder-khiimori-text-muted backdrop-blur-xl"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value as TabGroupFilter)}
                className={filterSelectClass}
                aria-label="Filter by group"
              >
                <option value="all">All tabs</option>
                <option value="active">Active only</option>
                <option value="clustered">In a group</option>
                <option value="ungrouped">Ungrouped</option>
              </select>

              <select
                value={securityFilter}
                onChange={(e) => setSecurityFilter(e.target.value as SecurityFilter)}
                className={filterSelectClass}
                aria-label="Filter by security"
              >
                <option value="all">Any security</option>
                <option value="safe">Safe</option>
                <option value="warning">Warning</option>
                <option value="danger">Threat</option>
                <option value="unscanned">Not scanned</option>
              </select>

              <select
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value)}
                className={`${filterSelectClass} max-w-[11rem]`}
                aria-label="Filter by site"
              >
                <option value="all">All sites</option>
                {siteOptions.map((host) => (
                  <option key={host} value={host}>
                    {host}
                  </option>
                ))}
              </select>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-3 py-3 text-sm text-khiimori-text-muted hover:text-khiimori-text-primary border border-khiimori-border/50 rounded-xl hover:bg-khiimori-bg-elevated/60 transition-colors"
                >
                  Clear
                </button>
              )}

              <div className="flex bg-khiimori-bg-elevated/50 border border-khiimori-border/50 rounded-xl p-1 backdrop-blur-xl">
                {(['clusters', 'grid', 'list'] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      viewMode === mode
                        ? 'bg-primary-500 text-khiimori-text-primary shadow-lg shadow-primary-500/20'
                        : 'text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-tertiary/50'
                    }`}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>

              <Button
                variant="glass"
                size="sm"
                onClick={() => navigate('/tabs?section=preview')}
                leftIcon={<Eye className="w-4 h-4" />}
              >
                Preview
              </Button>

              <Button
                variant="glass"
                size="sm"
                onClick={() => workspaceSessionId && fetchTabs(workspaceSessionId)}
                leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
                disabled={!workspaceSessionId || isLoading}
              >
                Refresh
              </Button>

              {selectedTabs.size > 0 && (
                <Badge variant="primary" size="md">
                  {selectedTabs.size} selected
                </Badge>
              )}
            </div>
          </div>

          {hasActiveFilters && (
            <p className="text-xs text-khiimori-text-muted">
              Showing {filteredTabs.length} of {tabs.length} tabs
              {searchQuery.trim() ? ` matching “${searchQuery.trim()}”` : ''}
            </p>
          )}
        </div>

        <EmbeddedSkillRail
          cardClassName="mt-4"
          title="Suggested actions"
          sessionId={workspaceSessionId ?? undefined}
          skillIds={['auto_organize', 'auto_suspend_idle', 'close_duplicates', 'group_by_domain', 'dead_tab_cleanup', 'analyze_workspace']}
          onSkillExecuted={handleSkillExecuted}
          preferExecute
        >
          <WorkspacePatternsPanel
            patterns={workspacePatterns}
            onDismiss={() => setWorkspacePatterns([])}
          />
        </EmbeddedSkillRail>
      </div>

      {/* Bulk Actions */}
      {selectedTabs.size > 0 && (
        <Card padding="sm" className="mb-6 bg-primary-500/5 border-primary-500/20 flex items-center justify-between">
          <span className="text-sm font-medium text-primary-300">
            {selectedTabs.size} tab{selectedTabs.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <select
              className="px-3 py-1.5 bg-khiimori-bg-elevated/50 border border-khiimori-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/50 text-khiimori-text-primary text-sm backdrop-blur-xl [&>option]:bg-khiimori-bg-elevated"
              disabled={isGeneratingApp}
              onChange={(e) => {
                if (e.target.value) {
                  void handleBulkGenerateApp(e.target.value as AppGenerationType);
                  e.target.value = "";
                }
              }}
              defaultValue=""
            >
              <option value="" disabled>Generate App...</option>
              {APP_GENERATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="h-4 w-px bg-khiimori-bg-tertiary/50 mx-1" />
            <Button variant="glass" size="sm" leftIcon={<GitCompare className="w-4 h-4" />} onClick={() => void handleBulkCompare()} disabled={isGeneratingApp}>
              {isGeneratingApp && activeGenerationType === 'comparison' ? (generationMessage ?? 'Generating…') : 'Compare'}
            </Button>
            <Button variant="glass" size="sm" leftIcon={<FileText className="w-4 h-4" />} onClick={() => void handleBulkSynthesize()} disabled={isGeneratingApp}>
              {isGeneratingApp && activeGenerationType === 'research_summary' ? (generationMessage ?? 'Generating…') : 'Synthesize'}
            </Button>
            <Button variant="glass" size="sm" leftIcon={<BookOpen className="w-4 h-4" />} onClick={() => void handleBulkResearchPaper()} disabled={isGeneratingApp}>
              {isGeneratingApp && activeGenerationType === 'research_paper' ? (generationMessage ?? 'Generating…') : 'Research Paper'}
            </Button>
            <Button variant="glass" size="sm" leftIcon={<Bookmark className="w-4 h-4" />} onClick={handleBulkSave}>
              Save
            </Button>
          </div>
        </Card>
      )}

      {/* Error Banner */}
      {fetchError && !isLoading && (
        <Card padding="lg" className="mb-6 border-red-500/30 bg-red-500/5">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-300">Failed to load tabs</h3>
              <p className="text-xs text-red-400/80 mt-1">{fetchError}</p>
            </div>
            <Button
              variant="glass"
              size="sm"
              onClick={() => workspaceSessionId && fetchTabs(workspaceSessionId)}
            >
              Retry
            </Button>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !fetchError && tabs.length === 0 && (
        <Card padding="lg" className="mb-6">
          <div className="text-center py-8">
            <Monitor className="w-12 h-12 text-khiimori-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-khiimori-text-secondary mb-2">No tabs to display</h3>
            <p className="text-sm text-khiimori-text-muted max-w-md mx-auto mb-4">
              Install the Khiimori browser extension and open some tabs to see them here.
            </p>
            <Button
              variant="glass"
              size="sm"
              onClick={() => workspaceSessionId && fetchTabs(workspaceSessionId)}
              leftIcon={<RefreshCw className="w-4 h-4" />}
            >
              Refresh
            </Button>
          </div>
        </Card>
      )}

      {/* Clusters View */}
      {viewMode === 'clusters' && (
        <div className="space-y-6">
          {clusters.map((cluster) => (
            <Card key={cluster.id} padding="lg">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-500/20 rounded-lg">
                    <Sparkles className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-khiimori-text-primary">{cluster.name}</h3>
                    <p className="text-sm text-khiimori-text-muted">{cluster.pattern} ~ {cluster.tabs.length} tabs</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="success">
                    {(cluster.confidence * 100).toFixed(0)}% match
                  </Badge>
                  <select
                    value={clusterAppTypes[cluster.id] ?? defaultAppTypeForCluster(cluster.pattern)}
                    onChange={(e) =>
                      setClusterAppTypes((prev) => ({
                        ...prev,
                        [cluster.id]: e.target.value as AppGenerationType,
                      }))
                    }
                    disabled={generatingClusterId === cluster.id}
                    className="px-3 py-1.5 bg-khiimori-bg-elevated/50 border border-khiimori-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/50 text-khiimori-text-primary text-sm backdrop-blur-xl [&>option]:bg-khiimori-bg-elevated disabled:opacity-50"
                    aria-label={`App type for ${cluster.name}`}
                  >
                    {APP_GENERATION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={
                      generatingClusterId === cluster.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4" />
                      )
                    }
                    onClick={() => {
                      const appType =
                        clusterAppTypes[cluster.id] ?? defaultAppTypeForCluster(cluster.pattern);
                      void handleClusterGenerateApp(cluster, appType);
                    }}
                    disabled={generatingClusterId === cluster.id}
                  >
                    Generate App
                  </Button>
                </div>
              </div>

              {/* Generation Progress Indicator */}
              {generatingClusterId === cluster.id && (() => {
                const progressKey = `${workspaceSessionId}-${cluster.id}`;
                const progress = generationProgress[progressKey];
                return (
                  <div className="mb-4 p-3 bg-khiimori-bg-elevated/60 border border-primary-500/30 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {progress?.stage === 'complete' ? (
                          <CheckCircle2 className="w-4 h-4 text-success-400" />
                        ) : progress?.stage === 'failed' ? (
                          <AlertCircle className="w-4 h-4 text-error-400" />
                        ) : (
                          <Loader2 className="w-4 h-4 text-primary-400 animate-spin" />
                        )}
                        <span className="text-sm font-medium text-khiimori-text-secondary">
                          {progress?.message ?? 'Starting...'}
                        </span>
                      </div>
                      <span className="text-sm text-primary-400 font-mono">
                        {progress?.percent ?? 0}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-khiimori-bg-tertiary/50 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-400 transition-all duration-300"
                        style={{ width: `${progress?.percent ?? 0}%` }}
                      />
                    </div>
                    {progress?.appId && (
                      <div className="mt-2 flex items-center gap-2">
                        <a
                          href={`/apps/${progress.appId}`}
                          className="text-sm text-primary-400 hover:text-primary-300 underline"
                        >
                          View generated app
                        </a>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cluster.tabs.map((tab) => (
                  <TabCard
                    key={tab.id}
                    tab={tab}
                    isSelected={selectedTabs.has(tab.id)}
                    onToggleSelect={() => toggleTabSelection(tab.id)}
                    onAnalyze={() => analyzeTab(tab.id)}
                    onDelete={() => handleDeleteTab(tab.id)}
                    isAnalyzing={isAnalyzing}
                  />
                ))}
              </div>
            </Card>
          ))}

          {/* Unclustered Tabs — create groups here instead of the Groups tab */}
          <Card padding="lg" className="bg-khiimori-bg-secondary/40">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-khiimori-text-primary">Unclustered Tabs</h3>
                <p className="text-sm text-khiimori-text-muted">
                  {unclusteredTabs.length === 0
                    ? 'All tabs are grouped'
                    : `${unclusteredTabs.length} tab${unclusteredTabs.length === 1 ? '' : 's'} not in a group`}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Folder className="w-4 h-4" />}
                  onClick={handleOpenCreateGroupModal}
                  disabled={selectedUnclusteredTabIds.length === 0}
                >
                  Create group
                  {selectedUnclusteredTabIds.length > 0 ? ` (${selectedUnclusteredTabIds.length})` : ''}
                </Button>
                <Button
                  variant="glass"
                  size="sm"
                  leftIcon={<Sparkles className="w-4 h-4" />}
                  onClick={() => void handleAISuggestGroups()}
                >
                  AI suggest groups
                </Button>
              </div>
            </div>
            {unclusteredTabs.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {unclusteredTabs.map((tab) => (
                  <TabCard
                    key={tab.id}
                    tab={tab}
                    isSelected={selectedTabs.has(tab.id)}
                    onToggleSelect={() => toggleTabSelection(tab.id)}
                    onAnalyze={() => analyzeTab(tab.id)}
                    onDelete={() => handleDeleteTab(tab.id)}
                    isAnalyzing={isAnalyzing}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-khiimori-text-muted py-4 text-center">
                Use AI suggestions to discover new groupings, or ungroup tabs from existing clusters.
              </p>
            )}
          </Card>
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {filteredTabs.map((tab) => (
            <TabCard
              key={tab.id}
              tab={tab}
              isSelected={selectedTabs.has(tab.id)}
              onToggleSelect={() => toggleTabSelection(tab.id)}
              onAnalyze={() => analyzeTab(tab.id)}
              onDelete={() => handleDeleteTab(tab.id)}
              isAnalyzing={isAnalyzing}
            />
          ))}
        </div>
      )}

      {/* List View — includes security columns (formerly separate Security screen) */}
      {viewMode === 'list' && (
        <TabSecurityTable
          rows={securityTableRows}
          isLoading={isLoading}
          onScanAll={() => void handleScanAllSecurity()}
          isScanning={isScanningSecurity}
          selectedIds={selectedTabs}
          onToggleSelect={toggleTabSelection}
          onSelectAll={handleSelectAll}
          onDeleteTab={handleDeleteTab}
          searchable={false}
        />
      )}

      <TabAlertsPanel
        sessionId={workspaceSessionId}
        sessionTabs={alertSessionTabs}
        className="mt-6"
      />

      {/* Create Group Modal */}
      <Modal
        isOpen={showCreateGroupModal}
        onClose={() => {
          setShowCreateGroupModal(false);
          setNewGroupName('');
        }}
        title="Create Group"
        description={`Group ${selectedUnclusteredTabIds.length} selected tab${selectedUnclusteredTabIds.length === 1 ? '' : 's'}`}
        size="sm"
      >
        <div className="space-y-4">
          <Input
            type="text"
            label="Group name"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="e.g. Research, Shopping"
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreateGroup();
            }}
          />
          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setShowCreateGroupModal(false);
                setNewGroupName('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={() => void handleCreateGroup()}
              isLoading={isCreatingGroup}
              disabled={!newGroupName.trim()}
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>

      {/* AI Suggest Groups Modal */}
      <Modal
        isOpen={showAISuggestModal}
        onClose={() => setShowAISuggestModal(false)}
        title="AI Suggested Groups"
        description="Suggested groupings based on your open tabs"
        size="md"
      >
        <div className="space-y-3" data-testid="ai-suggested-groups">
          {aiLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-primary-400" />
              <span className="ml-3 text-sm text-khiimori-text-secondary">Analyzing tabs…</span>
            </div>
          ) : aiSuggestions.length > 0 ? (
            aiSuggestions.map((suggestion) => {
              const key = suggestionKey(suggestion);
              const isCreating = aiCreatingKey === key;
              const confidencePct = Math.round((suggestion.confidence || 0) * 100);
              return (
                <Card key={key} padding="sm" className="border border-khiimori-border/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-khiimori-text-primary">{suggestion.groupName}</p>
                      <p className="text-xs text-khiimori-text-muted mt-1">
                        {suggestion.count} tabs · {confidencePct}% confidence
                      </p>
                      {suggestion.reasoning && (
                        <p className="text-sm text-khiimori-text-secondary mt-2">{suggestion.reasoning}</p>
                      )}
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => void handleAcceptSuggestion(suggestion)}
                      isLoading={isCreating}
                      disabled={isCreating}
                    >
                      Create
                    </Button>
                  </div>
                </Card>
              );
            })
          ) : (
            <p className="text-sm text-khiimori-text-muted text-center py-6">
              No group suggestions right now. Try again after opening more related tabs.
            </p>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteTab}
        title="Delete Tab"
        message="Are you sure you want to delete this tab? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}

function TabCard({
  tab,
  isSelected,
  onToggleSelect,
  onAnalyze,
  onDelete,
  isAnalyzing
}: {
  tab: Tab;
  isSelected: boolean;
  onToggleSelect: () => void;
  onAnalyze: () => void;
  onDelete: () => void;
  isAnalyzing: boolean;
}) {
  return (
    <Card
      hoverable
      padding="sm"
      className={`relative border-2 transition-all ${
        isSelected ? 'border-primary-500/50 shadow-lg shadow-primary-500/10' : 'border-transparent'
      }`}
    >
      <div className="absolute top-3 left-3 z-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="w-4 h-4 rounded border-khiimori-border bg-khiimori-bg-secondary text-primary-500 focus:ring-primary-500/50 focus:ring-offset-khiimori-bg-secondary"
        />
      </div>

      <div className="absolute top-3 right-3 flex items-center gap-1">
        {tab.isActive && <Circle className="w-3 h-3 text-success-500 fill-current" />}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
          className="p-1 ml-1"
          title="Delete tab"
          aria-label="Delete tab"
        >
          <Trash2 className="w-3 h-3 text-khiimori-text-muted hover:text-error-400" />
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-3 mt-6">
        {tab.favicon && <img src={tab.favicon} alt="" className="w-5 h-5 rounded-sm" />}
        <h4 className="font-semibold text-khiimori-text-primary text-sm truncate">{tab.title}</h4>
      </div>

      <p className="text-xs text-khiimori-text-muted mb-3 truncate">{tab.url}</p>

      {tab.security && (
        <div className="flex items-center gap-2 mb-3">
          <SecurityScoreBar score={tab.security.score} compact />
          <EncryptionBadge encryption={tab.security.encryption} />
        </div>
      )}

      {tab.summary && (
        <p className="text-sm text-khiimori-text-secondary mb-3 line-clamp-2">{tab.summary}</p>
      )}

      {tab.entities && tab.entities.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {tab.entities.slice(0, 3).map((entity, idx) => (
            <Badge key={idx} variant="secondary" size="sm">
              {entity}
            </Badge>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-khiimori-border/50 mt-auto">
        <a
          href={tab.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1 transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          Open
        </a>
        <Button
          variant="ghost"
          size="sm"
          onClick={onAnalyze}
          disabled={isAnalyzing}
          className="text-sm"
          aria-label="Analyze tab"
        >
          {isAnalyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          Analyze
        </Button>
      </div>
    </Card>
  );
}

export default TabMonitor;